const budgets = Object.freeze({ low: 24, balanced: 80, ultra: 160 });

export function selectHDRType(THREE, renderer) {
  return renderer.extensions?.has('EXT_color_buffer_float')
    ? THREE.HalfFloatType : THREE.UnsignedByteType;
}

// Inject the caller's Three instance to avoid duplicate renderer/material classes.
export function createThreeEffects(THREE, parent, { seed = 1, quality = 'balanced', reducedMotion = false } = {}) {
  if (!Object.hasOwn(budgets, quality)) throw new RangeError('Unknown effects quality');
  const root = new THREE.Group();
  root.name = 'environment-effects';
  parent.add(root);
  let randomState = seed >>> 0;
  const random = () => { randomState = (Math.imul(randomState, 1664525) + 1013904223) >>> 0; return randomState / 4294967296; };
  const layers = [];
  let disposed = false;
  const createLayer = (kind, color, size, opacity) => {
    const positions = new Float32Array(160 * 3);
    for (let i = 0; i < 160; i++) {
      positions[i * 3] = (random() - 0.5) * (kind === 0 ? 4 : 90);
      positions[i * 3 + 1] = random() * (kind === 1 ? 5 : 12);
      positions[i * 3 + 2] = (random() - 0.5) * (kind === 0 ? 4 : 90);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const material = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false,
      uniforms: { time: { value: 0 }, tint: { value: new THREE.Color(color) }, alpha: { value: opacity }, size: { value: size } },
      vertexShader: `uniform float time; uniform float size; varying float fade;
        void main() {
          vec3 p = position;
          ${kind === 0 ? 'p.y = mod(p.y + time * 0.65, 12.0); p.x += sin(p.y * 0.6 + time * 0.2); fade = sin(p.y / 12.0 * 3.14159);' : kind === 1 ? 'p.x = mod(p.x + time * 0.4 + 45.0, 90.0) - 45.0; fade = 1.0;' : 'p.y = mod(p.y - time * 12.0, 12.0); fade = 0.5;'}
          vec4 view = modelViewMatrix * vec4(p, 1.0);
          gl_Position = projectionMatrix * view;
          gl_PointSize = clamp(size * 180.0 / max(1.0, -view.z), 1.0, 96.0);
        }`,
      fragmentShader: `uniform vec3 tint; uniform float alpha; varying float fade;
        void main() {
          vec2 p = gl_PointCoord * 2.0 - 1.0;
          ${kind === 2 ? 'p.x *= 4.0;' : ''}
          float a = (1.0 - smoothstep(0.1, 1.0, dot(p,p))) * alpha * fade;
          if (a < 0.003) discard;
          gl_FragColor = vec4(tint, a);
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
        }`
    });
    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    root.add(points);
    layers.push(points);
    return points;
  };
  const smoke = createLayer(0, 0x73818c, 5, 0.12);
  smoke.position.set(16, 0.5, -18);
  const clouds = createLayer(1, 0x72899f, 13, 0.045);
  clouds.position.y = 21;
  const rain = createLayer(2, 0x93b9d1, 0.8, 0.28);
  rain.visible = false;
  let wet = false;
  const setQuality = (preset, motion = false) => {
    if (!Object.hasOwn(budgets, preset)) throw new RangeError('Unknown effects quality');
    reducedMotion = Boolean(motion);
    root.userData.reducedMotion = reducedMotion;
    for (const layer of layers) layer.geometry.setDrawRange(0, reducedMotion ? 0 : budgets[preset]);
  };
  setQuality(quality, reducedMotion);
  return {
    root, smoke, clouds, rain,
    setQuality,
    setWeather(value) { wet = Boolean(value); rain.visible = wet; },
    update(time) {
      if (disposed || !Number.isFinite(time)) return;
      for (const layer of layers) layer.material.uniforms.time.value = reducedMotion ? 0 : time;
      rain.visible = wet && !reducedMotion;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      root.removeFromParent();
      for (const layer of layers) { layer.geometry.dispose(); layer.material.dispose(); }
    }
  };
}

export function enhanceSurface(material, { water = false, weathering = 0.18 } = {}) {
  if (!material.isMeshStandardMaterial) throw new TypeError('A standard/physical material is required');
  if (!Number.isFinite(weathering) || weathering < 0 || weathering > 1) throw new RangeError('Weathering must be in [0, 1]');
  const previousCompile = material.onBeforeCompile;
  const previousKey = material.customProgramCacheKey;
  const clock = { value: 0 };
  material.onBeforeCompile = function(shader, renderer) {
    previousCompile.call(this, shader, renderer);
    shader.uniforms.fxTime = clock;
    shader.vertexShader = 'varying vec3 fxPosition;\n' + shader.vertexShader;
    shader.vertexShader = shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\nfxPosition = position;');
    shader.fragmentShader = 'uniform float fxTime; varying vec3 fxPosition;\n' + shader.fragmentShader;
    const effect = water
      ? 'normal = normalize(normal + vec3(sin(fxPosition.x * 3.0 + fxTime) * 0.12, 0.0, cos(fxPosition.y * 4.0 - fxTime * 1.3) * 0.12));'
      : `float wear = sin(fxPosition.x * 9.0) * sin(fxPosition.y * 13.0) * sin(fxPosition.z * 7.0); diffuseColor.rgb *= 1.0 - ${weathering.toFixed(4)} * (wear * 0.5 + 0.5);`;
    shader.fragmentShader = shader.fragmentShader.replace('#include <normal_fragment_maps>', '#include <normal_fragment_maps>\n' + effect);
  };
  material.customProgramCacheKey = function() { return `${previousKey.call(this)}:surface-fx:${water}:${weathering}`; };
  material.needsUpdate = true;
  return {
    update(time) { if (Number.isFinite(time)) clock.value = time; },
    dispose() {
      material.onBeforeCompile = previousCompile;
      material.customProgramCacheKey = previousKey;
      material.needsUpdate = true;
    }
  };
}
